import Highcharts from 'highcharts/esm/highcharts.js';

export interface CustomLegendOptions extends Highcharts.LegendOptions {
    useScrollbars?: boolean;
}

// This is all based on this example from highcharts
// https://api.highcharts.com/highcharts/legend.navigation
//
// It's reliant of properties not defined on types. We must ensure anytime the Highcharts package is updated
// that the legends with scrollbars are tested to ensure nothing has broken.
//
// The logic is controlled by the custom property 'useScrollbars' on the legend.
// I tried doing an early return if it was false or if scrollbars were not needed,
// but that was causing render issues with the legend. I think it's currently safer to
// rely on the prop, as it limits the impacted charts and scope of testing to just certain charts.
//
// Originally an event was added to the Highcharts Chart class to handle the load, and then if
// applicable, it would add the 'afterRender' event to the chart legend instance. That way only
// charts that needed a scrollable legend would have the 'afterRender' event. However, a rare intermittent
// issue was discovered where the 'afterRender' event didn't fire on initial load, resulting in the legend
// not having a scrollbar. Toggling the legend on and off fixed it. This meant the event was registered,
// it just didn't fire in time. The only guess I can make is either Highcharts didn't fire it, in which case
// there's nothing to be done, or the event was not registered in time.
// Lifting the event registration from per instance and to the class seems to have resolved it.
// This means every chart has the event, but it should only ever do something if `useScrollbars` is true
export const OPRALegendScrollPlugin = (H: typeof Highcharts) => {
    const { defined } = H;

    // Making it an arrow function actually breaks functionality
    // eslint-disable-next-line func-names
    H.addEvent(H.Legend, 'afterRender', function () {
        const legend = this as Highcharts.Legend;
        const { useScrollbars, useHTML } = legend.options as CustomLegendOptions;

        if (defined(useScrollbars) && useScrollbars && useHTML && legend.group.div) {
            if (!legend.legendWrapper) {
                // Create additional SVG element to put inside additional div
                // after first render
                legend.legendWrapper = legend.chart.renderer.createElement('svg').attr({
                    version: '1.1',
                    class: 'highcharts-scrollable-legend',
                    height: legend.legendHeight,
                    width: legend.legendWidth,
                });
            }

            const { element } = legend.legendWrapper;
            // Move legend group to the new SVG element
            legend.group.add(legend.legendWrapper);
            // Add SVG element to div
            legend.group.div.appendChild(element);
            // Add style to use native browser scrollbar
            legend.group.div.style.overflow = 'auto';

            const chartWidth = legend.chart.getContainerBox().width;
            const maxWidth = chartWidth > legend.legendWidth ? legend.legendWidth : chartWidth;
            const applyScroll = chartWidth < legend.legendWidth;
            legend.group.div.style.maxWidth = `${maxWidth}px`;
            legend.align();
            if (applyScroll) legend.group.div.style.left = 0;
            legend.group.element.removeAttribute('transform');
        }
    });

    // We have to tidy up the custom legend, otherwise it can leave empty scrollable containers behind
    // It does also look like there  might be a bug in Highcharts when useHTML = true for legends. As it always
    // seems to leave behind divs, resulting in multiple legend div containers the more the legend is toggled
    // eslint-disable-next-line func-names
    H.wrap(H.Legend.prototype, 'destroy', function (proceed, ...args) {
        const legend = this as Highcharts.Legend;
        proceed.apply(legend, Array.prototype.slice.call(args, 1));

        if (legend.chart) {
            const { useScrollbars } = legend.options as CustomLegendOptions;
            if (defined(useScrollbars) && useScrollbars && legend.legendWrapper?.element) {
                legend.legendWrapper.element?.remove();
            }
        }
    });

    return H;
};
